import { Settings as SettingsIcon, Database, Webhook, Car, Info, Map, User as UserIcon, Loader2, HelpCircle, Zap, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VERSION_HISTORY } from '@/lib/version';
import { ActivityTemplateManager } from '@/components/ActivityTemplateManager';
import { PilotUserList } from '@/components/PilotUserList';
import { NotificationsSettings } from '@/components/NotificationsSettings';
import { InviteTeammateCard } from '@/components/InviteTeammateCard';
import { TeamGoalsCard } from '@/components/settings/TeamGoalsCard';
import { CommunicationsCard } from '@/components/comms/CommunicationsCard';
import { MessageTemplatesCard } from '@/components/comms/MessageTemplatesCard';
import { TeamCommunicationsCard } from '@/components/comms/TeamCommunicationsCard';
import { DealershipRulesCard } from '@/components/settings/DealershipRulesCard';
import { PilotHealthLinkCard } from '@/components/settings/PilotHealthLinkCard';
import { CrmImportCard } from '@/components/settings/CrmImportCard';
import { BillingCard } from '@/components/BillingCard';
import { BillingHistoryCard } from '@/components/BillingHistoryCard';
import { BillingDetailsCard } from '@/components/BillingDetailsCard';
import { TeamSubscriptionAudit } from '@/components/TeamSubscriptionAudit';
import { AccessCallout } from '@/components/AccessCallout';
import { SeatManagementCard } from '@/components/SeatManagementCard';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const integrations = [
  {
    name: 'Secure Data',
    desc: 'Database, authentication, and real-time data sync.',
    icon: Database,
    status: 'Connected',
    connected: true,
  },
  {
    name: 'Automate Sales',
    desc: 'Follow-up automations, SMS/email sequences, and appointment booking and confirming.',
    icon: Webhook,
    status: 'Available',
    connected: false,
  },
  {
    name: 'Dealer CRM Bridge',
    desc: 'Sync deal data with your existing DMS or CRM system.',
    icon: Car,
    status: 'Coming Soon',
    connected: false,
  },
];

export default function SettingsPage() {
  const { isSolo, mode, dealershipName, dealershipId, refreshProfile } = useAuth();
  const [workspaceName, setWorkspaceName] = useState(dealershipName);
  const [savingName, setSavingName] = useState(false);
  const isRealSolo = mode === 'real' && isSolo;

  const handleSaveWorkspace = async () => {
    if (!dealershipId || !workspaceName.trim()) return;
    setSavingName(true);
    const { error } = await supabase
      .from('dealerships')
      .update({ name: workspaceName.trim() })
      .eq('id', dealershipId);
    setSavingName(false);
    if (error) {
      toast.error('Could not rename workspace');
      return;
    }
    toast.success('Workspace renamed');
    await refreshProfile();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-20 lg:pb-0">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><SettingsIcon className="h-6 w-6 text-primary" /> Settings & Integrations</h1>
        <p className="text-sm text-muted-foreground">Manage your dealership connections</p>
      </div>

      {isRealSolo && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserIcon className="h-4 w-4 text-primary" /> Your Solo Workspace
            </CardTitle>
            <CardDescription>
              You're running PullOver as a solo rep. Rename your workspace anytime — only you can see it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace name</Label>
              <div className="flex gap-2">
                <Input
                  id="workspace-name"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="My Workspace"
                  maxLength={60}
                />
                <Button
                  onClick={handleSaveWorkspace}
                  disabled={savingName || !workspaceName.trim() || workspaceName.trim() === dealershipName}
                >
                  {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Joining a real dealership later? Ask their manager to send you an invite from their Settings.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Solo referral nudge — appears after 3+ sold deals, self-hides when dismissed or multi-seat */}
      <InviteTeammateCard />

      {/* Communications — connect email + show texting number assignment (real mode only) */}
      <CommunicationsCard />

      {/* Team communications — manager assigns numbers + Teams config (manager-only, real-mode, self-hides) */}
      <TeamCommunicationsCard />

      {/* Reusable message templates — manager/admin only, real-mode only (component self-hides) */}
      <MessageTemplatesCard />

      {/* Billing & subscription */}
      <AccessCallout />
      <SeatManagementCard />
      <BillingCard />
      <BillingHistoryCard />
      <BillingDetailsCard />
      <TeamSubscriptionAudit />

      <Link to="/roadmap" className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary/50 transition-colors group">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Map className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">View pilot roadmap</p>
            <p className="text-xs text-muted-foreground">See what's next after the demo</p>
          </div>
        </div>
        <span className="text-sm text-primary group-hover:translate-x-0.5 transition-transform">→</span>
      </Link>

      <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/10">
        <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium">Pull Over Sales does not replace your CRM.</p>
          <p className="text-sm text-muted-foreground">It enhances visibility and triggers communications. Think of it as your execution layer on top of existing processes.</p>
        </div>
      </div>

      <div className="space-y-4">
        {integrations.map(int => (
          <Card key={int.name}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                    <int.icon className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{int.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{int.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant={int.connected ? 'default' : 'secondary'}>{int.status}</Badge>
                  <Button variant={int.connected ? 'outline' : 'default'} size="sm" disabled={int.status === 'Coming Soon'}>
                    {int.connected ? 'Configure' : int.status === 'Coming Soon' ? 'Coming Soon' : 'Connect'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Automation Events */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Automation Events</CardTitle>
          <CardDescription>Events that can trigger automations via GHL or webhooks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              'demo_logged', 'appointment_set', 'test_drive_done', 'quote_review_logged',
              'deal_stalled', 'deal_sold', 'customer_feedback_green', 'customer_feedback_yellow', 'customer_feedback_red',
            ].map(event => (
              <div key={event} className="px-3 py-2 bg-muted/30 rounded text-xs font-mono text-muted-foreground">{event}</div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Activity Templates (manager-only, self-hides) */}
      <ActivityTemplateManager />

      {/* Operating manual: goals, rules, pilot health (manager-only, self-hides) */}
      <TeamGoalsCard />
      <DealershipRulesCard />
      <PilotHealthLinkCard />
      <CrmImportCard />

      {/* Help & positioning */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-primary" /> How PullOver fits your day
          </CardTitle>
          <CardDescription>Don't remember anything. Just log it.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="flex gap-3">
            <Zap className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Capture the up in 5 seconds</p>
              <p className="text-muted-foreground">
                Snap the license, type a name, hit save. The deal is alive — even before you sit down at the desk.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Layers className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Sits next to Dealer180 / CDK — doesn't replace them</p>
              <p className="text-muted-foreground">
                Keep using your DMS for desking, F&amp;I and inventory. PullOver owns the floor: who walked in,
                who's hot, what's the next step. When the deal closes, write it up in CDK or Dealer180 like always.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">One rule: every deal has a next step</p>
              <p className="text-muted-foreground">
                We nag you at create and at every stage advance. That's the whole reason ups stop slipping
                through the cracks.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Push notifications (real mode only, self-hides) */}
      <NotificationsSettings />

      {/* Team members (manager-only, real mode, self-hides) */}
      <PilotUserList />

      {/* Version History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Version History</CardTitle>
          <CardDescription>Release notes and changelog</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {VERSION_HISTORY.map(entry => (
              <div key={entry.version} className="border-l-2 border-border pl-4 py-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="font-mono">{entry.version}</Badge>
                  <span className="text-sm font-medium">{entry.title}</span>
                  {entry.date && <span className="text-xs text-muted-foreground ml-auto">{entry.date}</span>}
                </div>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-0.5">
                  {entry.summary.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
